import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useConfirmationContext } from "@contexts/ConfirmationContext";
import { useTutorialContext } from "@contexts/TutorialContext";
import UserService from "@services/UserService";
import User, { UserApiCredential } from "@ltypes/user";
import Card from "@components/ui/Card/Card";
import TextField from "@components/forms/TextField/TextField";
import Button from "@components/forms/Button/Button";
import { updateUser } from "@slices/userSlice";
import { FaCircleQuestion, FaSpotify, FaUpRightFromSquare } from "react-icons/fa6";
import Api from "@enums/api";
import Badge from "@components/ui/Badge/Badge";


function ExternalApisPage() {
    const [formApis, setFormApis] = useState<{[key: string]: {[key2: string]: any}}>({
        SPOTIFY: {}
    });
    const [searchParams, setSearchParams] = useSearchParams();
    
    const user: User|null = useSelector((state: any) => state.user);
    const dispatch = useDispatch();

    const { t } = useTranslation();
    const { openConfirmationModal } = useConfirmationContext();
    const { openTutorialModal } = useTutorialContext();

    const service = new UserService;

    const credentials: {[label: string]: UserApiCredential|null} = {
        SPOTIFY: user?.api_credentials.find((value: UserApiCredential) => value.type === Api.SPOTIFY) ?? null,
    };

    const handleChangeFormApi = (api: string, key: string, value: any) => {
        setFormApis({
            ...formApis,
            [api]: {
                ...formApis[api],
                [key]: value,
            }
        })
    };

    const handleCreateCredential = (api: string) => {
        const promise = service.createCredential(api.toLocaleLowerCase(), formApis.SPOTIFY.client_id, formApis.SPOTIFY.client_secret);

        toast.promise(promise, {
            loading: t('toast.adding'),
            success: () => t('toast.credentials_added'),
            error: err => err.body.message || t('toast.error_fetching'),
        });

        promise.then(data => {
            dispatch(updateUser(data.body));
        }).catch(e => {
        });
    };

    const handleDeleteCredential = (api: string) => {
        openConfirmationModal(t('page.account.api_credentials.delete_credentials_confirmation'), () => {
            const promise = service.deleteCredential(api.toLocaleLowerCase());

            toast.promise(promise, {
                loading: t('toast.deleting'),
                success: () => t('toast.deleted'),
                error: err => err.body.message || t('toast.error_fetching'),
            });

            promise.then(data => {
                dispatch(updateUser(data.body));
            }).catch(e => {
            });
        });
    };

    const handleAuthorize = (api: string) => {
        if(api === Api.SPOTIFY && credentials.SPOTIFY !== null) {
            window.location.href = `https://accounts.spotify.com/en/authorize?client_id=${credentials.SPOTIFY.client_id}&response_type=code&redirect_uri=${encodeURIComponent(process.env.REACT_APP_API_URL!)}/callback/spotify&scope=user-read-currently-playing`;
        }
    };

    useEffect(() => {
        if(searchParams.has('error')) {
            toast.error(`${t('text.error')} : ${searchParams.get('error')}`);
            searchParams.delete('error');
            setSearchParams(searchParams);
        }
    }, []);

    return (
        <>
            <div className="p-3">
                <h1>{t('title.external_apis')}</h1>

                <Card>
                    <div className="row g-3">
                        <div className="col-12">
                            <Card color="primary">
                                <div className="row m-0">
                                    <div className="col-12">
                                        <div className="row gy-2 align-items-center">
                                            <div className="col-auto"><FaSpotify size="3rem"/></div>
                                            <div className="col">
                                                <h2>Spotify</h2>
                                            </div>
                                            <div className="col-auto">
                                                <Button size="sm" className="secondary" onClick={() => openTutorialModal('api_spotify')}><FaCircleQuestion size="1.1rem"/> {t('text.tutorial')}</Button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        {credentials.SPOTIFY !== null ? (
                                            <div className="row gy-2 mt-2">
                                                <div className="col-12 col-md-7">
                                                    client id : <code>{credentials.SPOTIFY.client_id}</code>
                                                </div>
                                                <div className="col-12 col-md-5">
                                                    <div className="row g-2 align-items-center justify-content-end">
                                                        <div className="col-auto">
                                                            <Button className="red" size="sm" onClick={() => handleDeleteCredential(Api.SPOTIFY)}>{t('text.delete')}</Button>
                                                        </div>
                                                        <div className="col-auto">
                                                        {!credentials.SPOTIFY.authorized ? (
                                                            <Button className="primary" size="sm" onClick={() => handleAuthorize(Api.SPOTIFY)}><FaSpotify size="1.5rem"/> {t('page.account.api_credentials.connect_acount')}</Button>
                                                        ) : (
                                                            <Badge color="green">{t('page.account.api_credentials.account_linked')}</Badge>
                                                        )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="row gy-2 mt-2">
                                                <div className="col-12 col-md-4">
                                                    <TextField label="client id" value={formApis.SPOTIFY.client_id ?? ''} onChange={(value: string) => handleChangeFormApi(Api.SPOTIFY, 'client_id', value)}/>
                                                </div>
                                                <div className="col-12 col-md-4">
                                                    <TextField label="client secret" value={formApis.SPOTIFY.client_secret ?? ''} onChange={(value: string) => handleChangeFormApi(Api.SPOTIFY, 'client_secret', value)}/>
                                                </div>
                                                <div className="col-12 col-md-4 align-self-end">
                                                    <Button size="sm" onClick={() => handleCreateCredential(Api.SPOTIFY)} disabled={!formApis.SPOTIFY.client_id || !formApis.SPOTIFY.client_secret}>{t('text.add')}</Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </Card>
            </div>
        </>
    );
}

export default ExternalApisPage;
